import React, { useCallback, useEffect, useState } from 'react';

import { Tooltip } from '@mui/material';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import { useContentEditorTextEntities } from 'components/ContentEditor/context/ContentEditorTextEntitiesContext';
import { getResolvedTextEntityTooltipValue } from 'components/ContentEditor/utils/getResolvedTextEntityTooltipValue';
import { translate as t } from 'hooks/useTranslations';

const TEXT_ENTITY_SELECTOR = 'span.text-entity';
const ENTITY_KEY_ATTR = 'data-phx-text-entity-key';

function readEntityKeyFromSpan(el: HTMLElement): string {
  const fromAttr = el.getAttribute(ENTITY_KEY_ATTR);
  if (fromAttr != null && fromAttr.trim() !== '') {
    return fromAttr.trim();
  }
  return (el.textContent ?? '').trim();
}

type TextEntityTooltipPluginProps = {
  textEntitiesEnabled: boolean;
};

export default function TextEntityTooltipPlugin({ textEntitiesEnabled }: TextEntityTooltipPluginProps) {
  const [editor] = useLexicalComposerContext();
  const textEntities = useContentEditorTextEntities();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [title, setTitle] = useState('');

  const active = textEntitiesEnabled && textEntities !== undefined;

  const updateHover = useCallback(
    (el: HTMLElement | null) => {
      if (!el || !active) {
        setAnchorEl(null);
        setTitle('');
        return;
      }
      const key = readEntityKeyFromSpan(el);
      const resolved = getResolvedTextEntityTooltipValue(textEntities, key);
      setAnchorEl(el);
      setTitle(resolved ?? t('contentEditor.textEntity.valueNotSet'));
    },
    [active, textEntities],
  );

  useEffect(() => {
    if (!active) {
      setAnchorEl(null);
      setTitle('');
      return;
    }

    const root = editor.getRootElement();
    if (!root) {
      return;
    }

    const onPointerOver = (e: PointerEvent) => {
      const rawTarget = e.target;
      if (!(rawTarget instanceof Node) || !root.contains(rawTarget)) {
        return;
      }
      const element = rawTarget instanceof Element ? rawTarget : rawTarget.parentElement;
      if (!element) {
        return;
      }
      const hit = element.closest(TEXT_ENTITY_SELECTOR) as HTMLElement | null;
      if (hit && root.contains(hit)) {
        updateHover(hit);
      }
    };

    const onPointerOut = (e: PointerEvent) => {
      const rawTarget = e.target;
      if (!(rawTarget instanceof Node)) {
        return;
      }
      const element = rawTarget instanceof Element ? rawTarget : rawTarget.parentElement;
      if (!element) {
        return;
      }
      const hit = element.closest(TEXT_ENTITY_SELECTOR) as HTMLElement | null;
      if (!hit || !root.contains(hit)) {
        return;
      }
      const related = e.relatedTarget as Node | null;
      if (!related || !hit.contains(related)) {
        updateHover(null);
      }
    };

    root.addEventListener('pointerover', onPointerOver);
    root.addEventListener('pointerout', onPointerOut);

    return () => {
      root.removeEventListener('pointerover', onPointerOver);
      root.removeEventListener('pointerout', onPointerOut);
    };
  }, [editor, active, updateHover]);

  if (!active) {
    return null;
  }

  return (
    <Tooltip
      title={title}
      open={Boolean(anchorEl) && title !== ''}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      slotProps={{
        popper: {
          anchorEl: anchorEl ?? undefined,
          disablePortal: false,
        },
      }}
    >
      <span
        style={{
          position: 'fixed',
          width: 0,
          height: 0,
          pointerEvents: 'none',
          bottom: 0,
          left: 0,
        }}
        aria-hidden
      />
    </Tooltip>
  );
}
